import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { motion } from "framer-motion";
import { Mail, Lock, Loader } from "lucide-react";

import Input from "../components/Input.jsx";
import { useAuthStore } from "../store/authStore";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const redirectMessage = location.state?.message;

  const { login, isLoading, error, clearSessionExpired } = useAuthStore();

  // Arriving here proves the expiry redirect committed, so it is now safe to
  // clear the flag (the note itself comes from location.state, not the store).
  useEffect(() => {
    if (redirectMessage) clearSessionExpired();
  }, [redirectMessage, location.key, clearSessionExpired]);

  const handleLogin = async (e) => {
    e.preventDefault();
    await login(email, password);
    navigate("/");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
      className="max-w-md w-full mt-28 mb-28 bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-xl rounded-2xl shadow-xl overflow-hidden"
    >
      <div className="p-8">
        <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-cyan-700 to-cyan-800 text-transparent bg-clip-text ">
          Login
        </h2>
        {redirectMessage && (
          <p className="text-amber-400 text-sm text-center mb-4">
            {redirectMessage}
          </p>
        )}
        <form onSubmit={handleLogin}>
          <Input
            icon={Mail}
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            icon={Lock}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="flex items-center mb-6">
            <Link
              to="forgotPassword"
              className="mt-4 text-sm text-cyan-500 hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          {error && <p className="text-red-500 font-semibold mb-2">{error}</p>}

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-3 px-4 bg-gradient-to-r from-cyan-700 to-cyan-800 text-white font-bold rounded-lg shadow-lg hover:from-cyan-800 hover:to-cyan-900 focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:ring-offset-2 focus:ring-offset-gray-900 transition duration-200"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader className="w-6 h-6 animate-spin mx-auto" />
            ) : (
              "Login"
            )}
          </motion.button>
        </form>
      </div>
      <div className="px-8 py-4 bg-gray-900 bg-opacity-50 flex flex-col items-center gap-2">
        <p className="text-sm text-gray-400">
          Don&apos;t have an account?{" "}
          <Link to="/auth/signup" className="text-cyan-500 hover:underline">
            Sign up
          </Link>
        </p>
        {redirectMessage && (
          <Link to="/" className="text-sm text-cyan-500 hover:underline">
            Continue as guest
          </Link>
        )}
      </div>
    </motion.div>
  );
};

export default LoginPage;
